import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/modern-design-system.css'; // Import modern design system
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Auth Components
import Login from './components/auth/Login';
import PrivateRoute from './components/auth/PrivateRoute';

// Personnel Dashboard Components - Modern & Legacy
import PersonnelDashboard from './components/personnel/Dashboard';
import PersonnelDashboardModern from './components/personnel/DashboardModern';
import CheckInOut from './components/personnel/CheckInOut';
import PersonnelStatistics from './components/personnel/Statistics';
import PersonnelSettings from './components/personnel/Settings';
import PersonnelNewClients from './components/personnel/NewClients';
import PersonnelIncidentReport from './components/personnel/IncidentReport';
import PersonnelSpecialAssignment from './components/personnel/SpecialAssignment';

// Admin Dashboard Components - Modern & Legacy
import AdminDashboard from './components/admin/Dashboard';
import AdminDashboardModern from './components/admin/DashboardModern';
import PersonnelManagement from './components/admin/PersonnelManagement';
import SiteManagement from './components/admin/SiteManagement';
import AdminStatistics from './components/admin/Statistics';
import SubAdminManagement from './components/admin/SubAdminManagement';
import Settings from './components/admin/Settings';
import ReportDetails from './components/admin/ReportDetails';
import AdminNewClients from './components/admin/NewClients';
import CoverageView from './components/admin/CoverageView';
import StructureManagement from './components/admin/StructureManagement';
import PersonnelCoverage from './components/personnel/Coverage';
import AdminIncidentReports from './components/admin/IncidentReports';
import AdminSpecialAssignments from './components/admin/AdminSpecialAssignments';

// Super Admin Components
import SuperAdminRoutes from './components/superadmin/SuperAdminRoutes';

// Common Components
import Sidebar from './components/common/Sidebar';
import ModernSidebar from './components/common/ModernSidebar';
import TopNavbar from './components/common/TopNavbar';
import NotificationBell from './components/common/NotificationBell';

// Services
import websocketService from './services/websocketService';
import notificationService from './services/notificationService';

function App() {
  // Track current user for service initialization
  const [currentUser, setCurrentUser] = useState(null);
  const servicesInitializedFor = useRef(null);

  // Check for user on mount and when storage changes
  useEffect(() => {
    const checkUser = () => {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      const token = localStorage.getItem('token');
      
      if (user && token) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    };

    // Check initially
    checkUser();

    // Listen for storage changes (e.g., from login)
    window.addEventListener('storage', checkUser);
    
    // Also listen for custom login event
    window.addEventListener('userLoggedIn', checkUser);

    return () => {
      window.removeEventListener('storage', checkUser);
      window.removeEventListener('userLoggedIn', checkUser);
    };
  }, []);

  // Initialize services when user changes
  useEffect(() => {
    const initializeServices = async () => {
      const user = currentUser;
      const token = localStorage.getItem('token');
      
      if (!user || !token) {
        // User logged out - clear services
        if (servicesInitializedFor.current) {
          websocketService.disconnect();
          notificationService.clear();
          servicesInitializedFor.current = null;
        }
        return;
      }

      // Check if we already initialized for this user
      if (servicesInitializedFor.current === user.id) {
        return;
      }

      // Clear any existing connections
      if (servicesInitializedFor.current) {
        websocketService.disconnect();
        notificationService.clear();
      }
      
      if (user.role === 'admin' || user.role === 'sub-admin' || user.role === 'personnel') {
        const companyId = user.companyId || user.company_id;

        // Decode the JWT to get the authoritative user ID
        let userId = user.id;
        try {
          const decoded = jwtDecode(token);
          userId = decoded.id ?? decoded.sub ?? decoded.userId ?? user.id;
        } catch (_) {
          // Use user.id from localStorage if JWT decode fails
        }

        websocketService.connect(userId, companyId);
        await notificationService.initialize(userId, user.name || user.email, companyId);
        
        servicesInitializedFor.current = user.id;
      }
    };

    initializeServices();

    return () => {
      // Cleanup on unmount
      if (servicesInitializedFor.current) {
        websocketService.disconnect();
        notificationService.clear();
        servicesInitializedFor.current = null;
      }
    };
  }, [currentUser]); // Re-run when currentUser changes

  // Create layout components for admin, personnel, and super admin dashboards
  const AdminLayout = ({ children }) => (
    <div className="dashboard-container">
      <ModernSidebar role="admin" />
      <div className="modern-content-wrapper">
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          zIndex: 1000
        }}>
          <NotificationBell />
        </div>
        {children}
      </div>
    </div>
  );

  const PersonnelLayout = ({ children }) => (
    <div className="dashboard-container">
      <ModernSidebar role="personnel" />
      <div className="modern-content-wrapper">
        {children}
      </div>
    </div>
  );

  const SuperAdminLayout = ({ children }) => (
    <div className="dashboard-container">
      <ModernSidebar role="super-admin" />
      <div className="modern-content-wrapper">
        {children}
      </div>
    </div>
  );

  return (
    <Router>
      <div className="App">
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Navigate to="/login" />} />

          {/* Super Admin Routes with Layout and PrivateRoute protection */}
          <Route path="/superadmin/*" element={
            <PrivateRoute allowedRoles={['super-admin']}>
              <SuperAdminLayout>
                <SuperAdminRoutes />
              </SuperAdminLayout>
            </PrivateRoute>
          } />

          {/* Admin Routes with Layout and PrivateRoute protection */}
          {/* Modern Admin Dashboard */}
          <Route path="/admin" element={
            <PrivateRoute allowedRoles={['admin', 'sub-admin']}>
              <AdminLayout><AdminDashboardModern /></AdminLayout>
            </PrivateRoute>
          } />
          {/* Legacy Admin Dashboard */}
          <Route path="/admin/legacy" element={
            <PrivateRoute allowedRoles={['admin', 'sub-admin']}>
              <AdminLayout><AdminDashboard /></AdminLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/personnel" element={
            <PrivateRoute allowedRoles={['admin', 'sub-admin']}>
              <AdminLayout><PersonnelManagement /></AdminLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/sites" element={
            <PrivateRoute allowedRoles={['admin', 'sub-admin']}>
              <AdminLayout><SiteManagement /></AdminLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/statistics" element={
            <PrivateRoute allowedRoles={['admin', 'sub-admin']}>
              <AdminLayout><AdminStatistics /></AdminLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/subadmins" element={
            <PrivateRoute allowedRoles={['admin', 'sub-admin']}>
              <AdminLayout><SubAdminManagement /></AdminLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/settings" element={
            <PrivateRoute allowedRoles={['admin', 'sub-admin']}>
              <AdminLayout><Settings /></AdminLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/reports/:reportId" element={
            <PrivateRoute allowedRoles={['admin', 'sub-admin']}>
              <AdminLayout><ReportDetails /></AdminLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/clients" element={
            <PrivateRoute allowedRoles={['admin', 'sub-admin']}>
              <AdminLayout><AdminNewClients /></AdminLayout>
            </PrivateRoute>
          } />
          <Route coverage path="/admin/coverage" element={
            <PrivateRoute allowedRoles={['admin', 'sub-admin']}>
              <AdminLayout><CoverageView /></AdminLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/structure" element={
            <PrivateRoute allowedRoles={['admin', 'sub-admin']}>
              <AdminLayout><StructureManagement /></AdminLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/incident-reports" element={
            <PrivateRoute allowedRoles={['admin', 'sub-admin']}>
              <AdminLayout><AdminIncidentReports /></AdminLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/special-assignments" element={
            <PrivateRoute allowedRoles={['admin', 'sub-admin']}>
              <AdminLayout><AdminSpecialAssignments /></AdminLayout>
            </PrivateRoute>
          } />

          {/* Personnel Routes with Layout and PrivateRoute protection */}
          {/* Modern Personnel Dashboard */}
          <Route path="/personnel" element={
            <PrivateRoute allowedRoles={['personnel']}>
              <PersonnelLayout><PersonnelDashboardModern /></PersonnelLayout>
            </PrivateRoute>
          } />
          {/* Legacy Personnel Dashboard */}
          <Route path="/personnel/legacy" element={
            <PrivateRoute allowedRoles={['personnel']}>
              <PersonnelLayout><PersonnelDashboard /></PersonnelLayout>
            </PrivateRoute>
          } />
          <Route path="/personnel/check" element={
            <PrivateRoute allowedRoles={['personnel']}>
              <PersonnelLayout><CheckInOut /></PersonnelLayout>
            </PrivateRoute>
          } />
          <Route path="/personnel/statistics" element={
            <PrivateRoute allowedRoles={['personnel']}>
              <PersonnelLayout><PersonnelStatistics /></PersonnelLayout>
            </PrivateRoute>
          } />
          <Route path="/personnel/settings" element={
            <PrivateRoute allowedRoles={['personnel']}>
              <PersonnelLayout><PersonnelSettings /></PersonnelLayout>
            </PrivateRoute>
          } />
          <Route path="/personnel/clients" element={
            <PrivateRoute allowedRoles={['personnel']}>
              <PersonnelLayout><PersonnelNewClients /></PersonnelLayout>
            </PrivateRoute>
          } />
          <Route path="/personnel/coverage" element={
            <PrivateRoute allowedRoles={['personnel']}>
              <PersonnelLayout><PersonnelCoverage /></PersonnelLayout>
            </PrivateRoute>
          } />
          <Route path="/personnel/incident-report" element={
            <PrivateRoute allowedRoles={['personnel']}>
              <PersonnelLayout><PersonnelIncidentReport /></PersonnelLayout>
            </PrivateRoute>
          } />
          <Route path="/personnel/special-assignments" element={
            <PrivateRoute allowedRoles={['personnel']}>
              <PersonnelLayout><PersonnelSpecialAssignment /></PersonnelLayout>
            </PrivateRoute>
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
